import { ESdChangeReason, IDatabaseTableModel } from "@kuika/kuika-cl-model";
import ELK from "elkjs/lib/elk.bundled";
import { debounce } from "lodash";
import React from "react";
import ReactFlow, {
  Background,
  Controls,
  Edge,
  MarkerType,
  MiniMap,
  Node,
  NodeTypes,
  Panel,
  ReactFlowInstance,
  ReactFlowProvider
} from "reactflow";
import "reactflow/dist/style.css";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import ArrowDownSvg from "../../../svgs/small-icons/arrow-down.svg";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaPopover from "../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { SchemaNode } from "../left-panel/datasource-panel/datasources";
import { OrthogonalEdge } from "./orthogonal-edge";
import { columnRowCentreY, sortTableColumns } from "./table-card-geometry";
import { TableNode } from "./table-node";

// Performance-only styles - hover/interaction styles are in database-diagram.scss
// Note: Avoid will-change on multiple elements as it creates GPU compositor layers
// that can cause flickering in unrelated parts of the UI during zoom/pan operations
const edgeHoverStyles = `
  .react-flow__background {
    pointer-events: none;
  }

  .react-flow-table-node .icon-container svg,
  .react-flow__node .icon-container svg {
    width: 100% !important;
    height: 100% !important;
  }
`;

// Inject styles
if (typeof document !== "undefined") {
  const styleId = "reactflow-edge-hover-styles";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = edgeHoverStyles;
    document.head.appendChild(style);
  }
}

const nodeTypes: NodeTypes = {
  tableNode: TableNode as any
};

// ReactFlow configuration constants (extracted to prevent new references on each render)
const defaultEdgeOptions = {
  type: "orthogonal",
  animated: false
};

const edgeTypes = {
  orthogonal: OrthogonalEdge
};

const proOptions = { hideAttribution: true };

const translateExtent: [[number, number], [number, number]] = [
  [-40000, -40000],
  [80000, 80000]
];

const nodeExtent: [[number, number], [number, number]] = [
  [-40000, -40000],
  [80000, 80000]
];

const nodeOrigin: [number, number] = [0, 0];

const miniMapStyle = { display: "flex" };

const containerStyle = {
  width: "100%",
  height: "100%",
  position: "relative",
  overflow: "hidden"
} as const;

// Zoom controls component - zoom display updated via DOM to avoid re-renders
interface ZoomControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitView: () => void;
  onZoomTo: (level: number) => void;
}

interface ZoomControlsState {
  isMenuOpen: boolean;
}

interface ZoomAction {
  label: string;
  run: () => void;
}

class ZoomControls extends React.PureComponent<ZoomControlsProps, ZoomControlsState> {
  constructor(props: ZoomControlsProps) {
    super(props);
    this.state = { isMenuOpen: false };
  }

  setMenuOpen = (isMenuOpen: boolean): void => {
    this.setState({ isMenuOpen });
  };

  getZoomActions = (): ZoomAction[] => {
    const { onZoomIn, onZoomOut, onFitView, onZoomTo } = this.props;

    return [
      { label: "Zoom in", run: onZoomIn },
      { label: "Zoom out", run: onZoomOut },
      { label: "Fit to screen", run: onFitView },
      { label: "100%", run: () => onZoomTo(1) },
      { label: "200%", run: () => onZoomTo(2) },
      { label: "500%", run: () => onZoomTo(5) }
    ];
  };

  renderMenu = (): JSX.Element => (
    <div className="kuika_database_diagram__zoom-menu">
      {this.getZoomActions().map((action: ZoomAction) => (
        <KuikaButton
          key={action.label}
          type="tertiary"
          color="neutral"
          size="sm"
          onClick={() => {
            action.run();
            this.setMenuOpen(false);
          }}
        >
          {action.label}
        </KuikaButton>
      ))}
    </div>
  );

  render() {
    const { onZoomIn, onZoomOut } = this.props;

    return (
      <div className="kuika_database_diagram__zoom-settings">
        <KuikaButton type="text" size="sm" isIcon aria-label="Zoom out" onClick={onZoomOut}>
          <KuikaSvg name="minus" width={16} height={16} pointer />
        </KuikaButton>
        <div className="zoom-settings__dropdown-container">
          {/* Radix-positioned on purpose: rc-align (KuikaDropdownMenu) never resolves a
              position for a trigger inside a react-flow Panel — the popup hangs off-screen. */}
          <KuikaPopover
            hideCloseIcon
            position="bottom"
            align="center"
            sideOffset={6}
            visible={this.state.isMenuOpen}
            onVisibleChange={this.setMenuOpen}
            className="zoom-settings__menu-popover"
            content={this.renderMenu()}
          >
            <div className="zoom-settings__dropdown">
              <span id="diagram-zoom-display">100%</span>
              <ArrowDownSvg />
            </div>
          </KuikaPopover>
        </div>
        <KuikaButton type="text" size="sm" isIcon aria-label="Zoom in" onClick={onZoomIn}>
          <KuikaSvg name="plus" width={16} height={16} pointer />
        </KuikaButton>
      </div>
    );
  }
}

// Datasource label component
class DatasourceLabel extends React.PureComponent {
  render() {
    return (
      <span
        className="kuika_database_diagram__ds-name"
        data-testid="databaseDiagramDatasourceName"
        style={{ background: "var(--dark-0)", padding: "8px 12px", borderRadius: "var(--radius-m)" }}
      >
        Managed DB
      </span>
    );
  }
}

// Error display component
interface ErrorDisplayProps {
  error: string;
}

class ErrorDisplay extends React.PureComponent<ErrorDisplayProps> {
  render() {
    const { error } = this.props;
    return (
      <div
        style={{
          position: "absolute",
          top: "10px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
          padding: "10px 20px",
          background: "rgba(255, 77, 79, 0.95)",
          color: "#fff",
          borderRadius: "var(--radius-overlay)",
          border: "1px solid #ff4d4f"
        }}
      >
        <p>Something went wrong!</p>
        <p style={{ fontSize: "14px", margin: 0 }}>{error}</p>
      </div>
    );
  }
}

// Empty state display component
class EmptyStateDisplay extends React.PureComponent {
  render() {
    return (
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          zIndex: 10,
          padding: "20px",
          background: "var(--dark-0)",
          borderRadius: "var(--radius-l)",
          border: "1px solid var(--dark-3)"
        }}
      >
        <p style={{ fontSize: "16px", color: "#8c8c8c", margin: 0 }}>No tables to display</p>
        <p style={{ fontSize: "14px", color: "#bfbfbf", marginTop: "8px" }}>Create a table to see it here</p>
      </div>
    );
  }
}

// Loading state display component
class LoadingDisplay extends React.PureComponent {
  render() {
    return (
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          zIndex: 10,
          padding: "20px",
          background: "var(--dark-0)",
          borderRadius: "var(--radius-l)",
          border: "1px solid var(--dark-3)"
        }}
      >
        <p style={{ fontSize: "16px", color: "#1890ff", margin: 0 }}>Loading diagram...</p>
        <p style={{ fontSize: "14px", color: "#bfbfbf", marginTop: "8px" }}>Computing layout</p>
      </div>
    );
  }
}

const elk = new ELK();
const elkOptions = {
  "elk.algorithm": "layered",
  "elk.direction": "DOWN",
  "elk.layered.spacing.nodeNodeBetweenLayers": "100",
  "elk.layered.spacing.edgeNodeBetweenLayers": "50",
  "elk.spacing.nodeNode": "80",
  "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
  "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
  "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
  "elk.separateConnectedComponents": "true",
  "elk.spacing.componentComponent": "150",
  // Routes edges through orthogonal channels instead of splines. ELK also
  // places nodes so those channels exist — the tidy-arrow half of the work.
  "elk.edgeRouting": "ORTHOGONAL"
};

// Relation hues (see --rel-1..6 in color-palette.scss). Every edge takes the hue
// of the table it points at, so all the arrows into KRole share one colour. The
// cards themselves are a single accent — colour as information lives only here,
// on the arrows, instead of being said twice.
const RELATION_HUE_COUNT = 6;

function assignTableHues(foreignKeys: any[]): Map<string, number> {
  const referenced: string[] = [];
  for (const fk of foreignKeys) {
    if (fk.refTableName && !referenced.includes(fk.refTableName)) referenced.push(fk.refTableName);
  }
  referenced.sort();
  const hues = new Map<string, number>();
  for (let i = 0; i < referenced.length; i++) {
    hues.set(referenced[i], (i % RELATION_HUE_COUNT) + 1);
  }
  return hues;
}

// Calculate dynamic node dimensions based on actual content.
// Mirrors the metrics in database-diagram.scss — keep the two in step.
function calculateNodeDimensions(table: IDatabaseTableModel): {
  width: number;
  height: number;
} {
  const CHAR_WIDTH = 7; // 12px Inter, average — column names
  const TITLE_CHAR_WIDTH = 7.8; // 13px Inter semibold — the table name reads wider
  const MONO_CHAR_WIDTH = 6.2; // 10px mono type label
  const MIN_WIDTH = 200;
  const ROW_CHROME = 58; // 24 padding + 18 gutter + 2 * 8 gap
  const HEADER_CHROME = 96; // 22 padding + 16 icon + 2 * 8 gap + lock + count chip
  const HEADER_HEIGHT = 46;
  const ROW_HEIGHT = 26;
  const BODY_PADDING = 6;

  const headerWidth = HEADER_CHROME + (table.name?.length || 0) * TITLE_CHAR_WIDTH;

  let maxColumnWidth = 0;
  const columns = table.columns || [];
  for (const col of columns) {
    const colNameWidth = (col.name?.length || 0) * CHAR_WIDTH;
    const typeWidth = (col.kuikaDataType?.length || 8) * MONO_CHAR_WIDTH;
    maxColumnWidth = Math.max(maxColumnWidth, ROW_CHROME + colNameWidth + typeWidth);
  }

  const width = Math.max(MIN_WIDTH, headerWidth, maxColumnWidth) + 20;
  const height = HEADER_HEIGHT + BODY_PADDING + columns.length * ROW_HEIGHT;

  return { width, height };
}

async function getLayoutedElements(
  tables: IDatabaseTableModel[],
  foreignKeys: any[]
): Promise<{ nodes: Node[]; edges: Edge[] }> {
  const sourceHandles = new Set<string>();
  const targetHandles = new Set<string>();

  const foreignKeysLength = foreignKeys.length;
  for (let i = 0; i < foreignKeysLength; i++) {
    const fk = foreignKeys[i];
    sourceHandles.add(`${fk.tableName}-${fk.columnName}`);
    targetHandles.add(`${fk.refTableName}-${fk.refColumnName || "Id"}`);
  }

  const defaultKuikaTableNames = KMainFunctions.getDefaultKuikaTableNames();
  const defaultTableSet = new Set(defaultKuikaTableNames);

  const tableHues = assignTableHues(foreignKeys);

  const nodes: Node[] = new Array(tables.length);
  for (let i = 0; i < tables.length; i++) {
    const table = tables[i];
    nodes[i] = {
      id: table.name,
      type: "tableNode",
      data: {
        table,
        sourceHandles,
        targetHandles,
        isClickable: !defaultTableSet.has(table.name)
      },
      position: { x: 0, y: 0 }
    };
  }

  const tableNameSet = new Set(tables.map((t) => t.name));
  const validForeignKeys = foreignKeys.filter(
    (fk) => tableNameSet.has(fk.tableName) && tableNameSet.has(fk.refTableName)
  );

  const edgeCountMap = new Map<string, number>();

  const edges: Edge[] = new Array(validForeignKeys.length);
  for (let i = 0; i < validForeignKeys.length; i++) {
    const fk = validForeignKeys[i];
    const pairKey = `${fk.tableName}-${fk.refTableName}`;
    const currentCount = edgeCountMap.get(pairKey) || 0;
    edgeCountMap.set(pairKey, currentCount + 1);

    // Colour carries meaning: an edge takes the hue of the table it points at,
    // so every arrow into KRole shares one hue. The old `i % 6` cycle spent the
    // strongest visual signal on nothing — and painted some relations red.
    const targetHue = tableHues.get(fk.refTableName);
    const color = targetHue ? `var(--rel-${targetHue})` : "var(--dark-4)";
    const sourceHandleId = `${fk.tableName}-${fk.columnName}`;
    const targetColumnName = fk.refColumnName || "Id";
    const targetHandleId = `${fk.refTableName}-${targetColumnName}`;

    edges[i] = {
      id: `e-${fk.tableName}-${fk.refTableName}-${i}`,
      source: fk.tableName,
      target: fk.refTableName,
      sourceHandle: sourceHandleId,
      targetHandle: targetHandleId,
      type: "orthogonal",
      animated: false,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 12,
        height: 12,
        color
      },
      style: {
        stroke: color,
        strokeWidth: 1.75,
        opacity: 0.9
      },
      zIndex: -1,
      labelStyle: {
        fontSize: 11,
        fill: color,
        fontWeight: 600
      },
      labelBgStyle: {
        fill: "#fff",
        fillOpacity: 0.95,
        padding: 4
      }
    };
  }

  // ELK only routes into shared channels when it knows where a wire actually
  // enters a node. Without ports it aims at the box, so its sections land on the
  // node border while our edges hang off per-column handles — hence the routing
  // was discarded until now. One port per participating handle, positioned from
  // the same geometry the card renders with (table-card-geometry.ts).
  const portSide = (handleId: string, side: "s" | "t"): string => `${handleId}::${side}`;

  const graphChildren = new Array(nodes.length);
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const { width, height } = calculateNodeDimensions(node.data.table);
    // The card used to size itself to its content while ELK was told an estimate,
    // so the east ports sat up to 150px inside the real right edge and every
    // route was rejected as stale. Pinning the card to the same number makes the
    // estimate true by construction.
    node.data.width = width;
    const ports: any[] = [];
    const orderedColumns = sortTableColumns(node.data.table.columns || []);
    for (let row = 0; row < orderedColumns.length; row++) {
      const handleId = `${node.id}-${orderedColumns[row].name}`;
      const y = columnRowCentreY(row);
      if (targetHandles.has(handleId)) {
        ports.push({
          id: portSide(handleId, "t"),
          width: 1,
          height: 1,
          x: 0,
          y,
          layoutOptions: { "elk.port.side": "WEST" }
        });
      }
      if (sourceHandles.has(handleId)) {
        ports.push({
          id: portSide(handleId, "s"),
          width: 1,
          height: 1,
          x: width,
          y,
          layoutOptions: { "elk.port.side": "EAST" }
        });
      }
    }

    graphChildren[i] = {
      id: node.id,
      width,
      height,
      ports,
      layoutOptions: ports.length ? { "elk.portConstraints": "FIXED_POS" } : undefined
    };
  }

  const graphEdges = new Array(edges.length);
  for (let i = 0; i < edges.length; i++) {
    const edge = edges[i];
    const sourcePort = edge.sourceHandle ? portSide(edge.sourceHandle, "s") : undefined;
    const targetPort = edge.targetHandle ? portSide(edge.targetHandle, "t") : undefined;
    graphEdges[i] = {
      id: edge.id,
      sources: [sourcePort || edge.source],
      targets: [targetPort || edge.target]
    };
  }

  const graph = {
    id: "root",
    layoutOptions: elkOptions,
    children: graphChildren,
    edges: graphEdges
  };

  console.log("Creating layout with", nodes.length, "nodes and", edges.length, "edges");

  try {
    const layoutedGraph = await elk.layout(graph);

    const childrenMap = new Map();
    if (layoutedGraph.children) {
      for (let i = 0; i < layoutedGraph.children.length; i++) {
        const child = layoutedGraph.children[i];
        childrenMap.set(child.id, child);
      }
    }

    const layoutedNodes = new Array(nodes.length);
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const layoutedNode = childrenMap.get(node.id);
      layoutedNodes[i] = {
        ...node,
        position: {
          x: layoutedNode?.x || 0,
          y: layoutedNode?.y || 0
        }
      };
    }

    // ELK's section coordinates are absolute in the root graph — the same space
    // react-flow positions nodes in — so they can be handed straight to the edge.
    const routes = new Map<string, { x: number; y: number }[]>();
    for (const layoutedEdge of layoutedGraph.edges || []) {
      const section = layoutedEdge.sections?.[0];
      if (!section) continue;
      routes.set(layoutedEdge.id, [section.startPoint, ...(section.bendPoints || []), section.endPoint]);
    }

    const routedEdges = edges.map((edge: Edge) => {
      const points = routes.get(edge.id);
      return points ? { ...edge, data: { ...edge.data, points } } : edge;
    });

    return { nodes: layoutedNodes, edges: routedEdges };
  } catch (error) {
    console.error("ELK layout error:", error);
    const layoutedNodes = new Array(nodes.length);
    for (let i = 0; i < nodes.length; i++) {
      layoutedNodes[i] = {
        ...nodes[i],
        position: {
          x: (i % 3) * 300,
          y: Math.floor(i / 3) * 250
        }
      };
    }
    return { nodes: layoutedNodes, edges };
  }
}

export interface IDatabaseDiagramState {
  zoom: number;
}

export interface IDatabaseDiagramProps {}

// ReactFlow Diagram Component
interface ReactFlowDiagramProps {
  sd: any;
}

interface ReactFlowDiagramState {
  nodes: Node[];
  edges: Edge[];
}

class ReactFlowDiagram extends React.Component<ReactFlowDiagramProps, ReactFlowDiagramState> {
  private reactFlowInstance: ReactFlowInstance | null = null;

  // WeakRef to avoid DOM queries on every update
  private zoomDisplayRef: WeakRef<HTMLElement> | null = null;

  private debouncedLoadDiagram = debounce(() => {
    this.loadDiagramInternal();
  }, 100);

  constructor(props: ReactFlowDiagramProps) {
    super(props);
    this.state = {
      nodes: [],
      edges: []
    };
  }

  // Hovering a table focuses its relations: the edges that touch it go to full
  // chroma, everything else drops to a whisper. Relative contrast reads far
  // better than brightening one line on its own.
  //
  // Only the EDGE array is rebuilt. Handing react-flow a fresh node array resets
  // its measured dimensions — the nodes fall back to `visibility: hidden` and the
  // edges stop rendering with them — so card emphasis is applied straight to the
  // DOM instead, which also costs no re-render across 80+ nodes.
  private focusedNodeId: string | null = null;

  private focusCache: { id: string | null; source: Edge[]; edges: Edge[] } | null = null;

  private buildFocusEdges(edges: Edge[], focusId: string | null): Edge[] {
    if (!focusId) return edges;
    if (this.focusCache?.id === focusId && this.focusCache.source === edges) return this.focusCache.edges;

    const focused = edges.map((edge: Edge) => {
      const isLit = edge.source === focusId || edge.target === focusId;
      return {
        ...edge,
        zIndex: isLit ? 1 : -1,
        style: { ...edge.style, strokeWidth: isLit ? 2.6 : 1.75, opacity: isLit ? 1 : 0.12 }
      };
    });

    this.focusCache = { id: focusId, source: edges, edges: focused };
    return focused;
  }

  private paintFocusedCards(focusId: string | null): void {
    const container = this.containerRef.current;
    if (!container) return;

    container.classList.toggle("is-focusing", !!focusId);
    const related = new Set<string>();
    if (focusId) {
      related.add(focusId);
      for (const edge of this.state.edges) {
        if (edge.source === focusId) related.add(edge.target);
        else if (edge.target === focusId) related.add(edge.source);
      }
    }

    const elements = container.querySelectorAll<HTMLElement>(".react-flow__node[data-id]");
    elements.forEach((element: HTMLElement) => {
      const id = element.getAttribute("data-id") || "";
      element.classList.toggle("is-focused", !!focusId && id === focusId);
      element.classList.toggle("is-related", !!focusId && id !== focusId && related.has(id));
      element.classList.toggle("is-dimmed", !!focusId && !related.has(id));
    });
  }

  onNodeMouseEnter = (_event: React.MouseEvent, node: Node) => {
    if (this.focusedNodeId === node.id) return;
    this.focusedNodeId = node.id;
    this.paintFocusedCards(node.id);
    this.forceUpdate();
  };

  onNodeMouseLeave = () => {
    if (this.focusedNodeId === null) return;
    this.focusedNodeId = null;
    this.paintFocusedCards(null);
    this.forceUpdate();
  };

  // Level of detail is a class on the container, not state: crossing a zoom
  // tier must not re-render 80+ nodes. Row heights stay put so the column
  // handles — and therefore the edges — never move.
  private applyZoomTier(zoom: number): void {
    const container = this.containerRef.current;
    if (!container) return;
    container.classList.toggle("is-zoom-mid", zoom < 0.6 && zoom >= 0.35);
    container.classList.toggle("is-zoom-far", zoom < 0.35);
  }

  private containerRef = React.createRef<HTMLDivElement>();

  // Get or cache zoom display element
  private getZoomDisplay(): HTMLElement | null {
    let element = this.zoomDisplayRef?.deref();
    if (!element) {
      element = document.getElementById("diagram-zoom-display") as HTMLElement;
      if (element) {
        this.zoomDisplayRef = new WeakRef(element);
      }
    }
    return element || null;
  }

  // Update zoom display via DOM to avoid React re-renders
  updateZoomDisplay = (zoom: number) => {
    this.applyZoomTier(zoom);
    const display = this.getZoomDisplay();
    if (display) {
      display.textContent = `${Math.round(zoom * 100)}%`;
    }
  };

  componentWillUnmount() {
    this.debouncedLoadDiagram.cancel();
  }

  componentDidMount() {
    this.debouncedLoadDiagram();
  }

  componentDidUpdate(prevProps: ReactFlowDiagramProps) {
    if (prevProps.sd?.databaseDiagramData !== this.props.sd?.databaseDiagramData) {
      this.debouncedLoadDiagram();
    }
  }

  loadDiagram = () => {
    this.debouncedLoadDiagram();
  };

  loadDiagramInternal = async () => {
    const { sd } = this.props;
    if (!sd?.databaseDiagramData?.leftMenuData) {
      return;
    }

    const tables = sd.databaseDiagramData.leftMenuData?.tables || [];
    const foreignKeys = sd.databaseDiagramData.leftMenuData?.foreignKeys || [];

    if (tables.length === 0) {
      return;
    }

    const { nodes: layoutedNodes, edges: layoutedEdges } = await getLayoutedElements(tables, foreignKeys);

    this.setState({ nodes: layoutedNodes, edges: layoutedEdges });

    setTimeout(() => {
      if (layoutedNodes.length > 0 && this.reactFlowInstance) {
        this.reactFlowInstance.fitView({ padding: 0.2, duration: 200 });
        setTimeout(() => {
          if (this.reactFlowInstance) {
            this.updateZoomDisplay(this.reactFlowInstance.getZoom());
          }
        }, 250);
      }
    }, 100);
  };

  getManagedDB = () => {
    return this.props.sd?.datasources?.find((d: any) => d.connectionString === "");
  };

  findTableFromSchema = (tableName: string) => {
    const defaultKuikaTableNames = KMainFunctions.getDefaultKuikaTableNames();
    if (defaultKuikaTableNames.includes(tableName)) {
      return null;
    }
    const table: IDatabaseTableModel | undefined = this.props.sd.databaseDiagramData.leftMenuData?.tables.find(
      (t: any) => t.name === tableName
    );
    return table;
  };

  onNodeClick = (event: React.MouseEvent, node: Node) => {
    const { sd } = this.props;
    const tableName = node.id;
    let table: any;

    // Find the table in schemas
    Array.from(sd.schemas.values())?.find((schema: any) => {
      table = schema.tables.find((t: any) => t.title === tableName);
      return table;
    });

    if (table) {
      const schema = this.findTableFromSchema(tableName);
      if (schema) {
        sd?.setEditingTablesDatasource(this.getManagedDB(), schema, false);
      }
    }
  };

  handleZoomIn = () => {
    if (this.reactFlowInstance) {
      this.reactFlowInstance.zoomIn();
      this.updateZoomDisplay(this.reactFlowInstance.getZoom());
    }
  };

  handleZoomOut = () => {
    if (this.reactFlowInstance) {
      this.reactFlowInstance.zoomOut();
      this.updateZoomDisplay(this.reactFlowInstance.getZoom());
    }
  };

  handleFitView = () => {
    if (this.reactFlowInstance) {
      this.reactFlowInstance.fitView({ padding: 0.2 });
      this.updateZoomDisplay(this.reactFlowInstance.getZoom());
    }
  };

  handleZoomTo = (zoomLevel: number) => {
    if (this.reactFlowInstance) {
      this.reactFlowInstance.zoomTo(zoomLevel);
      this.updateZoomDisplay(zoomLevel);
    }
  };

  // `onMove` fires throughout a pan/zoom — including the programmatic fitView
  // that `onMoveEnd` alone never reports, which left the level-of-detail tier
  // unset on first paint. Both handlers only write to the DOM, so tracking the
  // whole gesture costs no re-render.
  onMove = () => {
    if (this.reactFlowInstance) {
      this.updateZoomDisplay(this.reactFlowInstance.getZoom());
    }
  };

  onMoveEnd = () => {
    if (this.reactFlowInstance) {
      this.updateZoomDisplay(this.reactFlowInstance.getZoom());
    }
  };

  onInit = (instance: ReactFlowInstance) => {
    this.reactFlowInstance = instance;
    // fitView lands before the first move event, so seed the zoom tier here or
    // the initial paint shows full detail at a zoom that cannot render it.
    this.updateZoomDisplay(instance.getZoom());
  };

  render() {
    const { nodes, edges: baseEdges } = this.state;
    const edges = this.buildFocusEdges(baseEdges, this.focusedNodeId);
    const { sd } = this.props;
    const loading = sd?.isDatabaseDiagramDataLoading;
    const error = sd?.databaseDiagramData?.error;
    const leftMenuData = sd?.databaseDiagramData?.leftMenuData;
    const tables = leftMenuData?.tables;

    // Show loading when: explicitly loading OR data hasn't been fetched yet (no leftMenuData)
    const showLoading = loading || !leftMenuData;
    // Show empty only when: not loading AND data was fetched AND no tables exist
    const showEmpty = !loading && leftMenuData && (!tables || tables.length === 0);

    return (
      <div
        className="kuika_database_diagram"
        data-testid="databaseDiagramCanvas"
        style={containerStyle}
        ref={this.containerRef}
      >
        {showLoading && <LoadingDisplay />}
        {showEmpty && <EmptyStateDisplay />}
        <Panel position="top-right">
          <ZoomControls
            onZoomIn={this.handleZoomIn}
            onZoomOut={this.handleZoomOut}
            onFitView={this.handleFitView}
            onZoomTo={this.handleZoomTo}
          />
        </Panel>
        <Panel position="top-left">
          <DatasourceLabel />
        </Panel>
        {error && error.length > 0 ? (
          <ErrorDisplay error={error} />
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodeClick={this.onNodeClick}
            onNodeMouseEnter={this.onNodeMouseEnter}
            onNodeMouseLeave={this.onNodeMouseLeave}
            onInit={this.onInit}
            onMove={this.onMove}
            onMoveEnd={this.onMoveEnd}
            fitView
            // Padding only — the opening view deliberately frames every table, even
            // though names are not readable at that scale. Reading is a zoom away.
            fitViewOptions={{ padding: 0.12 }}
            minZoom={0.1}
            maxZoom={5}
            snapToGrid={false}
            defaultEdgeOptions={defaultEdgeOptions}
            proOptions={proOptions}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            deleteKeyCode={null}
            selectionKeyCode={null}
            multiSelectionKeyCode={null}
            panActivationKeyCode={null}
            zoomActivationKeyCode={null}
            disableKeyboardA11y={true}
            translateExtent={translateExtent}
            nodeExtent={nodeExtent}
            elevateNodesOnSelect={false}
            elevateEdgesOnSelect={false}
            nodeOrigin={nodeOrigin}
            autoPanOnNodeDrag={false}
            panOnDrag={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
            zoomOnDoubleClick={false}
            preventScrolling={true}
            noDragClassName="nodrag"
            noWheelClassName="nowheel"
            selectNodesOnDrag={false}
          >
            <Background color="var(--dark-3)" gap={32} />
            <Controls showInteractive={false} />
            <MiniMap
              nodeColor="var(--blue-5)"
              maskColor="rgba(0, 0, 0, 0.1)"
              pannable={true}
              zoomable={true}
              style={miniMapStyle}
              nodeStrokeWidth={0}
            />
          </ReactFlow>
        )}
      </div>
    );
  }
}

// Wrapper class component to maintain compatibility
class DatabaseDiagram extends PureSdComponent<IDatabaseDiagramProps & IWithSDProps, IDatabaseDiagramState> {
  constructor(props: IDatabaseDiagramProps & IWithSDProps) {
    super(props);
    this.state = {
      zoom: 1
    };
  }

  componentDidMount() {
    // ReactFlow diagram initialized
  }

  getManagedDB = () => {
    const managedDB = this.props.sd?.datasources?.find((d) => d.connectionString == "");
    if (managedDB) {
      return managedDB;
    }
  };

  findTableFromSchema = (schema: SchemaNode) => {
    const defaultKuikaTableNames = KMainFunctions.getDefaultKuikaTableNames();
    if (defaultKuikaTableNames.includes(schema.title)) {
      return null;
    }
    const table: IDatabaseTableModel | undefined = this.props.sd.databaseDiagramData.leftMenuData?.tables.find(
      (t) => t.name === schema.title
    );
    return table;
  };

  render() {
    return (
      <ReactFlowProvider>
        <ReactFlowDiagram sd={this.props.sd} />
      </ReactFlowProvider>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const databaseDiagram = withSD<IDatabaseDiagramProps>(DatabaseDiagram);
export { databaseDiagram as DatabaseDiagram };
